import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { toast } from "sonner";
import {
  Plus, Trash2, X, Link2, AlertTriangle, CheckCircle2,
  XCircle, Copy, Check, Search, ChevronRight, Zap, Settings, Bot
} from "lucide-react";

const API = `${window.location.origin}/api`;

function formatDt(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit"
    });
  } catch { return "-"; }
}

function AliasModal({ prefillText, models, onSave, onClose }) {
  const [aliasText, setAliasText] = useState(prefillText || "");
  const [selectedModelId, setSelectedModelId] = useState("");
  const [modelSearch, setModelSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const filtered = models.filter(m =>
    !modelSearch ||
    m.model_name.toLowerCase().includes(modelSearch.toLowerCase()) ||
    m.model_key.includes(modelSearch.toLowerCase()) ||
    (m.brand || "").toLowerCase().includes(modelSearch.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!aliasText.trim() || !selectedModelId) {
      toast.error("Alias metni ve hedef model zorunludur");
      return;
    }
    setSaving(true);
    try {
      await onSave({ price_item_id: selectedModelId, alias_text: aliasText.trim() });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fade-in-up">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#DC2626]" />
            <h3 className="text-lg font-bold uppercase text-gray-900"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              Model Eşleştirmesi Ekle
            </h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400 hover:text-gray-700" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Alias text */}
          <div>
            <label className="block text-xs font-bold uppercase text-gray-500 tracking-wider mb-1.5">
              Müşteri Yazdığı Metin *
            </label>
            <input
              data-testid="alias-text-input"
              type="text"
              value={aliasText}
              onChange={e => setAliasText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#DC2626]"
              placeholder="Örn: nevcity50, new city 50, nwcty50..."
              autoFocus
            />
            {aliasText && (
              <p className="text-xs text-gray-400 mt-1">
                Normalize edilmiş: <code className="bg-gray-100 px-1 rounded">{
                  aliasText.toLowerCase()
                    .replace(/[şŞ]/g,"s").replace(/[ğĞ]/g,"g").replace(/[ıİ]/g,"i")
                    .replace(/[öÖ]/g,"o").replace(/[üÜ]/g,"u").replace(/[çÇ]/g,"c")
                    .replace(/[\s\-_]+/g,"").replace(/[^a-z0-9]/g,"")
                }</code>
              </p>
            )}
          </div>

          {/* Target model selector */}
          <div>
            <label className="block text-xs font-bold uppercase text-gray-500 tracking-wider mb-1.5">
              Hedef Model (Doğru Model) *
            </label>
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={modelSearch}
                onChange={e => setModelSearch(e.target.value)}
                placeholder="Model ara..."
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#DC2626]"
              />
            </div>
            <div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-6">Model bulunamadı</p>
              ) : (
                filtered.map(m => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setSelectedModelId(m.id)}
                    className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 transition-colors flex items-center justify-between border-b border-gray-50 last:border-0 ${
                      selectedModelId === m.id ? "bg-red-50 border-l-2 border-l-[#DC2626]" : ""
                    }`}
                  >
                    <div>
                      <span className="text-sm font-medium text-gray-800">{m.model_name}</span>
                      <span className="text-xs text-gray-400 ml-2 font-mono">{m.model_key}</span>
                    </div>
                    <span className="text-xs font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded shrink-0">{m.brand}</span>
                  </button>
                ))
              )}
            </div>
            {selectedModelId && (
              <div className="mt-2 flex items-center gap-2 text-xs text-green-700 bg-green-50 px-2 py-1.5 rounded">
                <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                <span>Seçilen: <strong>{models.find(m => m.id === selectedModelId)?.model_name}</strong></span>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-100 rounded-md p-3 text-xs text-blue-700">
            <p className="font-semibold mb-1">Nasıl çalışır?</p>
            <p>Müşteri <strong>"{aliasText || "nevcity50"}"</strong> yazdığında sistem otomatik olarak <strong>{models.find(m => m.id === selectedModelId)?.model_name || "seçili modeli"}</strong> bulacak ve fiyatını döndürecek.</p>
          </div>

          <div className="flex gap-3 pt-1">
            <button
              data-testid="alias-save-btn"
              type="submit"
              disabled={saving || !aliasText.trim() || !selectedModelId}
              className="flex-1 py-2.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold rounded-md disabled:opacity-50 transition-colors"
            >
              {saving ? "Kaydediliyor..." : "Eşleştir"}
            </button>
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 border border-gray-300 text-gray-600 text-sm rounded-md hover:bg-gray-50">
              İptal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ModelAliasesPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [aliases, setAliases] = useState([]);
  const [models, setModels] = useState([]);
  const [notFoundLogs, setNotFoundLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [prefillText, setPrefillText] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [modelBrandFilter, setModelBrandFilter] = useState("all");
  const [modelSearch, setModelSearch] = useState("");
  // Settings
  const [threshold, setThreshold] = useState(80);
  const [thresholdDraft, setThresholdDraft] = useState(80);
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [aliasRes, logRes, plRes, settingsRes] = await Promise.all([
        axios.get(`${API}/aliases`, { headers }),
        axios.get(`${API}/logs`, { headers, params: { found: false, limit: 30 } }),
        axios.get(`${API}/price-lists`, { headers }),
        axios.get(`${API}/settings`, { headers }),
      ]);
      setAliases(aliasRes.data);
      setNotFoundLogs(logRes.data.logs || []);

      const thr = settingsRes.data.find(s => s.key === "fuzzy_threshold");
      if (thr) { setThreshold(thr.value); setThresholdDraft(thr.value); }

      // Load models from active price list (all of them)
      const active = plRes.data.find(p => p.status === "ACTIVE");
      if (active) {
        const itemsRes = await axios.get(`${API}/price-lists/${active.id}/items`, {
          headers, params: { limit: 500 }
        });
        setModels(itemsRes.data.items || []);
      }
    } catch { toast.error("Veriler yüklenemedi"); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/settings`, { fuzzy_threshold: thresholdDraft }, { headers });
      setThreshold(thresholdDraft);
      toast.success(`Eşik değeri %${thresholdDraft} olarak kaydedildi`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kayıt başarısız");
    } finally { setSavingSettings(false); }
  };

  const handleCreate = async (data) => {
    try {
      await axios.post(`${API}/aliases`, data, { headers });
      toast.success("Eşleştirme eklendi");
      setShowModal(false);
      setPrefillText("");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ekleme başarısız");
      throw err;
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/aliases/${deleteId}`, { headers });
      toast.success("Eşleştirme silindi");
      setDeleteId(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silme başarısız");
    } finally { setDeleting(false); }
  };

  const handleApprove = async (id) => {
    setApprovingId(id);
    try {
      await axios.put(`${API}/aliases/${id}/approve`, {}, { headers });
      toast.success("Eşleştirme onaylandı — artık sorgularda aktif olarak kullanılıyor");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Onaylama başarısız");
    } finally { setApprovingId(null); }
  };

  const copyId = async (id, key) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(key);
      setTimeout(() => setCopiedId(null), 2000);
    } catch { toast.error("Kopyalanamadı"); }
  };

  // Group aliases by target model
  const grouped = {};
  aliases.forEach(a => {
    const k = a.target_model_key;
    if (!grouped[k]) grouped[k] = { model_name: a.target_model_name, brand: a.brand, model_id: a.target_model_id, items: [] };
    grouped[k].items.push(a);
  });

  // Filter not-found logs that don't already have an alias
  const aliasKeys = new Set(aliases.map(a => a.alias_key));
  const unmapped = notFoundLogs.filter(l => {
    const nk = (l.model_text || "").toLowerCase()
      .replace(/[şŞ]/g,"s").replace(/[ğĞ]/g,"g").replace(/[ıİ]/g,"i")
      .replace(/[öÖ]/g,"o").replace(/[üÜ]/g,"u").replace(/[çÇ]/g,"c")
      .replace(/[\s\-_]+/g,"").replace(/[^a-z0-9]/g,"");
    return !aliasKeys.has(nk);
  });

  return (
    <Layout>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 bg-[#DC2626] rounded-md flex items-center justify-center">
                <Link2 className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <h1 className="text-4xl font-bold uppercase text-gray-900 tracking-tight"
                style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                Model Eşleştirme
              </h1>
            </div>
            <p className="text-sm text-gray-500 mt-1 ml-12">
              Müşterilerin yanlış yazdığı model adlarını doğru modellerle eşleştirin
            </p>
          </div>
          <button
            data-testid="add-alias-btn"
            onClick={() => { setPrefillText(""); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold uppercase tracking-wide rounded-md transition-all active:scale-95"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            <Plus className="w-4 h-4" /> Eşleştirme Ekle
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-[#DC2626] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-bold uppercase text-gray-400 tracking-wider">Toplam Eşleştirme</p>
                <p className="text-3xl font-bold text-gray-900 mt-1" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                  {aliases.length}
                </p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-bold uppercase text-gray-400 tracking-wider">Otomatik Oluşturulan</p>
                <p className="text-3xl font-bold text-purple-600 mt-1" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                  {aliases.filter(a => a.auto_created).length}
                </p>
              </div>
              <div className="bg-white rounded-lg border border-amber-200 bg-amber-50 p-4">
                <p className="text-xs font-bold uppercase text-amber-600 tracking-wider">Eşleşmeyen Sorgu</p>
                <p className="text-3xl font-bold text-amber-600 mt-1" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                  {unmapped.length}
                </p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs font-bold uppercase text-gray-400 tracking-wider">Toplam Kullanım</p>
                <p className="text-3xl font-bold text-green-600 mt-1" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                  {aliases.reduce((s, a) => s + (a.hit_count || 0), 0)}
                </p>
              </div>
            </div>

            {/* How it works info */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
              <p className="text-xs font-bold uppercase text-blue-700 tracking-wider mb-1">Otomatik Eşleştirme Nasıl Çalışır?</p>
              <p className="text-sm text-blue-700">
                Müşteri yanlış yazdığında (örn: <code className="bg-blue-100 px-1 rounded">nevcsity</code>) sistem otomatik olarak en benzer modeli bulur 
                (mevcut eşik: <strong>%{threshold}</strong>). Eşleştirme oluşturulur ve bir dahaki sorguda direkt kullanılır. 
                Yanlış eşleşen otomatik alias'ları silebilirsiniz.
              </p>
            </div>

            {/* ── SETTINGS PANEL ─────────────────────────────── */}
            <div className="mb-6 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 bg-gray-50 border-b border-gray-100">
                <Settings className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
                <h2 className="text-sm font-bold uppercase tracking-wider text-gray-700"
                  style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                  Ayarlar
                </h2>
              </div>
              <div className="p-5">
                <div className="max-w-lg">
                  <label className="block text-xs font-bold uppercase text-gray-500 tracking-wider mb-1">
                    Fuzzy Eşleştirme Eşiği
                  </label>
                  <p className="text-xs text-gray-400 mb-4">
                    Otomatik model eşleştirmesi için minimum benzerlik skoru. Düşük = daha toleranslı ama yanlış eşleşme riski. Yüksek = daha az ama daha doğru eşleşme.
                  </p>

                  <div className="flex items-center gap-4">
                    {/* Slider */}
                    <div className="flex-1">
                      <input
                        data-testid="fuzzy-threshold-slider"
                        type="range"
                        min="50"
                        max="100"
                        step="5"
                        value={thresholdDraft}
                        onChange={e => setThresholdDraft(Number(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#DC2626]"
                      />
                      <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>%50 (toleranslı)</span>
                        <span>%75 (dengeli)</span>
                        <span>%100 (katı)</span>
                      </div>
                    </div>

                    {/* Value display */}
                    <div className="text-center shrink-0">
                      <div className={`text-3xl font-bold ${
                        thresholdDraft >= 85 ? "text-green-600" :
                        thresholdDraft >= 70 ? "text-[#DC2626]" : "text-amber-500"
                      }`} style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                        %{thresholdDraft}
                      </div>
                      <div className="text-xs text-gray-400">
                        {thresholdDraft >= 85 ? "Katı" : thresholdDraft >= 70 ? "Dengeli" : "Toleranslı"}
                      </div>
                    </div>
                  </div>

                  {/* Example */}
                  <div className="mt-4 p-3 bg-gray-50 rounded-md text-xs text-gray-600">
                    <span className="font-semibold">Örnek: </span>
                    {thresholdDraft <= 70
                      ? "\"boreli\", \"nevcsity\", \"brlliant\" gibi hatalar eşleşir. \"rasntomtext\" gibi alakasız metinler de eşleşebilir."
                      : thresholdDraft <= 85
                      ? "\"boreli\" (%92), \"nevcsity\" (%80) eşleşir. Çok farklı yazımlar eşleşmez."
                      : "Sadece çok yakın yazım hataları eşleşir. \"boreli\" → BORELLİ (%92) eşleşir ama \"nevcsity\" → NEWCİTY eşleşmeyebilir."
                    }
                  </div>

                  <div className="flex items-center gap-3 mt-4">
                    <button
                      data-testid="save-threshold-btn"
                      onClick={saveSettings}
                      disabled={savingSettings || thresholdDraft === threshold}
                      className="px-5 py-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {savingSettings ? "Kaydediliyor..." : "Kaydet"}
                    </button>
                    {thresholdDraft !== threshold && (
                      <button
                        onClick={() => setThresholdDraft(threshold)}
                        className="text-sm text-gray-500 hover:text-gray-700"
                      >
                        İptal
                      </button>
                    )}
                    {thresholdDraft === threshold && (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> Mevcut değer: %{threshold}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ── AUTO-ALIAS REVIEW ─────────────────────────── */}
            {aliases.filter(a => a.auto_created).length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Bot className="w-4 h-4 text-purple-600" />
                  <h2 className="text-base font-bold text-gray-800 uppercase tracking-wide"
                    style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                    Otomatik Eşleştirmeler — Gözden Geçir
                  </h2>
                  <span className="text-xs text-purple-600 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded font-bold">
                    {aliases.filter(a => a.auto_created).length} otomatik eşleştirme
                  </span>
                </div>

                <div className="mb-3 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-700">
                  Bu eşleştirmeler <strong>zaten aktif</strong> — sorgularda kullanılıyor. Yanlış eşleştirmeyi <strong>Reddet</strong> ile silin.
                </div>

                <div className="bg-white rounded-lg border border-purple-100 divide-y divide-gray-50">
                  {aliases.filter(a => a.auto_created).map(a => (
                    <div key={a.id} className="flex items-center justify-between px-4 py-3.5">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs text-purple-600 font-bold bg-purple-50 px-1.5 py-0.5 rounded-sm border border-purple-200">
                          🤖 Otomatik
                        </span>
                        <div className="flex items-center gap-1.5">
                          <code className="text-sm font-mono font-medium text-gray-800 bg-gray-100 px-2 py-0.5 rounded">
                            {a.alias_text}
                          </code>
                          <span className="text-gray-400">→</span>
                          <span className="text-sm font-semibold text-gray-800">{a.target_model_name}</span>
                          <span className="text-xs font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{a.brand}</span>
                        </div>
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded-sm ${
                          a.confidence >= 0.9 ? "text-green-600 bg-green-50 border border-green-200"
                          : a.confidence >= 0.8 ? "text-blue-600 bg-blue-50 border border-blue-200"
                          : "text-amber-600 bg-amber-50 border border-amber-200"
                        }`}>
                          %{Math.round((a.confidence || 0) * 100)} benzerlik
                        </span>
                        {a.hit_count > 0 && (
                          <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded-sm font-medium">
                            {a.hit_count} kez kullanıldı
                          </span>
                        )}
                      </div>
                      <button
                        data-testid={`reject-auto-alias-btn-${a.id}`}
                        onClick={() => setDeleteId(a.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-md transition-colors shrink-0"
                      >
                        <XCircle className="w-3.5 h-3.5" /> Reddet
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Unmatched queries */}
            {unmapped.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <h2 className="text-base font-bold text-gray-800 uppercase tracking-wide"
                    style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                    Eşleşmeyen Son Sorgular
                  </h2>
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded font-bold">
                    {unmapped.length} sorgu eşleşme bekliyor
                  </span>
                </div>
                <div className="bg-white rounded-lg border border-amber-200 divide-y divide-gray-50" data-testid="unmatched-queries">
                  {unmapped.slice(0, 10).map(log => (
                    <div key={log.id} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <XCircle className="w-4 h-4 text-red-400 shrink-0" />
                        <div>
                          <span className="font-medium text-gray-800 text-sm">{log.model_text}</span>
                          {log.brand && <span className="text-xs text-gray-400 ml-2">[{log.brand}]</span>}
                        </div>
                        <span className="text-xs text-gray-400">{formatDt(log.created_at)}</span>
                      </div>
                      <button
                        data-testid={`map-query-btn-${log.id}`}
                        onClick={() => { setPrefillText(log.model_text); setShowModal(true); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-bold rounded-md transition-colors"
                      >
                        <Link2 className="w-3 h-3" /> Eşleştir
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Model ID reference */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-[#DC2626]" />
                <h2 className="text-base font-bold text-gray-800 uppercase tracking-wide"
                  style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                  Model ID Referansları (Kommo için)
                </h2>
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{models.length} model</span>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-3 flex-wrap">
                  <p className="text-xs text-gray-600 flex-1">
                    Kommo'ya model ID öğrettikten sonra <code className="bg-gray-200 px-1 rounded">"model_id"</code> ile direkt sorgulayın.
                    Veya <code className="bg-gray-200 px-1 rounded">"model_key"</code> kullanın — liste değişse de geçerli kalır.
                  </p>
                </div>
                {/* Brand filter */}
                <div className="flex items-center gap-1 px-4 py-2 border-b border-gray-100 bg-white">
                  {[["all","Tümü"], ["KUBA","KUBA"], ["RKS","RKS"]].map(([val, lbl]) => (
                    <button key={val} onClick={() => setModelBrandFilter(val)}
                      className={`px-2.5 py-1 text-xs font-bold rounded-md transition-colors ${modelBrandFilter === val ? "bg-[#DC2626] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                      {lbl}
                    </button>
                  ))}
                  <div className="relative ml-2 flex-1 max-w-xs">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                    <input type="text" value={modelSearch} onChange={e => setModelSearch(e.target.value)}
                      placeholder="Model ara..."
                      className="w-full pl-7 pr-3 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#DC2626]" />
                  </div>
                  <span className="text-xs text-gray-400 ml-auto shrink-0">
                    {models.filter(m =>
                      (modelBrandFilter === "all" || m.brand === modelBrandFilter) &&
                      (!modelSearch || m.model_name.toLowerCase().includes(modelSearch.toLowerCase()) || m.model_key.includes(modelSearch.toLowerCase()))
                    ).length} / {models.length}
                  </span>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {models
                    .filter(m =>
                      (modelBrandFilter === "all" || m.brand === modelBrandFilter) &&
                      (!modelSearch || m.model_name.toLowerCase().includes(modelSearch.toLowerCase()) || m.model_key.includes(modelSearch.toLowerCase()))
                    )
                    .map(m => (
                    <div key={m.id} className="flex items-center justify-between px-4 py-2.5 border-b border-gray-50 hover:bg-gray-50">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded shrink-0 ${m.brand === "KUBA" ? "text-blue-600 bg-blue-50" : "text-orange-600 bg-orange-50"}`}>
                          {m.brand}
                        </span>
                        <span className="text-sm font-medium text-gray-800 truncate">{m.model_name}</span>
                        <span className="text-xs text-gray-400 font-mono shrink-0">{m.model_key}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        <code className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded max-w-[160px] truncate hidden md:block">
                          {m.id}
                        </code>
                        <button
                          data-testid={`copy-model-id-${m.id}`}
                          onClick={() => copyId(m.id, m.id)}
                          className="p-1 text-gray-400 hover:text-gray-700"
                          title="ID Kopyala"
                        >
                          {copiedId === m.id ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          onClick={() => copyId(m.model_key, `key_${m.id}`)}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="Model Key Kopyala (önerilen)"
                        >
                          {copiedId === `key_${m.id}` ? <Check className="w-3.5 h-3.5 text-green-500" /> : <span className="text-xs font-mono text-gray-400">key</span>}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* All aliases grouped by model */}
            {Object.keys(grouped).length === 0 ? (
              <div className="py-12 text-center text-gray-400 bg-white rounded-lg border border-gray-200">
                <Link2 className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm font-medium">Henüz eşleştirme yok.</p>
                <p className="text-xs text-gray-400 mt-1">Yukarıdaki "Eşleşmeyen Sorgular"dan başlayın.</p>
              </div>
            ) : (
              <div>
                <h2 className="text-base font-bold text-gray-800 uppercase tracking-wide mb-3"
                  style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                  Tüm Eşleştirmeler ({aliases.length})
                </h2>
                <div className="space-y-3" data-testid="aliases-list">
                  {Object.entries(grouped).map(([modelKey, group]) => (
                    <div key={modelKey} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100">
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                        <span className="text-xs font-bold text-gray-500 bg-white border border-gray-200 px-1.5 py-0.5 rounded">{group.brand}</span>
                        <span className="font-semibold text-gray-800 text-sm">{group.model_name}</span>
                        <span className="text-xs text-gray-400 font-mono">{modelKey}</span>
                        <span className="ml-auto text-xs text-[#DC2626] font-bold">{group.items.length} alias</span>
                      </div>
                      <div className="divide-y divide-gray-50">
                        {group.items.map(a => (
                          <div key={a.id} className={`flex items-center justify-between px-4 py-2.5 ${!a.approved ? "bg-amber-50/40" : ""}`}>
                            <div className="flex items-center gap-3 flex-wrap">
                              {a.auto_created && !a.approved && (
                                <span className="text-xs font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-sm border border-amber-200">⏳ Beklemede</span>
                              )}
                              {a.auto_created && a.approved && (
                                <span className="text-xs font-bold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-sm border border-purple-200">🤖 Onaylı {a.confidence ? `%${Math.round(a.confidence*100)}` : ""}</span>
                              )}
                              {!a.auto_created && (
                                <span className="text-xs font-bold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-sm">👤 Manuel</span>
                              )}
                              <code className="text-sm font-mono font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                                {a.alias_text}
                              </code>
                              {a.hit_count > 0 && (
                                <span className="text-xs text-green-600 font-bold bg-green-50 px-1.5 py-0.5 rounded-sm">
                                  {a.hit_count} kez kullanıldı
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {a.auto_created && !a.approved && (
                                <button
                                  onClick={() => handleApprove(a.id)}
                                  disabled={approvingId === a.id}
                                  className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
                                  title="Onayla"
                                >
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button
                                data-testid={`delete-alias-btn-${a.id}`}
                                onClick={() => setDeleteId(a.id)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                title="Sil"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Modals */}
        {showModal && (
          <AliasModal
            prefillText={prefillText}
            models={models}
            onSave={handleCreate}
            onClose={() => { setShowModal(false); setPrefillText(""); }}
          />
        )}

        {deleteId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full animate-fade-in-up">
              <h3 className="text-lg font-bold text-gray-900 mb-2"
                style={{ fontFamily: "Barlow Condensed, sans-serif" }}>Eşleştirmeyi Sil?</h3>
              <p className="text-sm text-gray-600 mb-5">
                Bu alias silinecek. Müşteri bu metni yazdığında model bulunamayacak.
              </p>
              <div className="flex gap-3">
                <button data-testid="confirm-delete-alias-btn"
                  onClick={handleDelete} disabled={deleting}
                  className="flex-1 py-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold rounded-md disabled:opacity-50">
                  {deleting ? "..." : "Sil"}
                </button>
                <button onClick={() => setDeleteId(null)}
                  className="flex-1 py-2 border border-gray-300 text-gray-600 text-sm rounded-md hover:bg-gray-50">
                  İptal
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
